import { supabase } from "@/lib/supabase";
import { friendlyError } from "@/lib/errors";

/**
 * Structured RPC / transport error.
 *
 * `supabase.rpc` rejects with a PostgREST error object that carries `code`,
 * `details`, `hint` and (often) a nested `context.status`. Wrapping that in a
 * plain `new Error(friendlyText)` used to discard every machine-readable field,
 * which silently disabled every downstream classifier that depends on a
 * SQLSTATE (for example product-create reconciliation).
 *
 * `message` stays operator-facing and friendly. The original machine-readable
 * fields are preserved for classification only and are never rendered as UI
 * copy. No credential, connection string or backend diagnostic is copied.
 */
export class RpcError extends Error {
  /** PostgREST / PostgreSQL SQLSTATE, when the server supplied one. */
  readonly code: string | null;
  /** Safe diagnostic detail from the server (never rendered as UI copy). */
  readonly details: string | null;
  readonly hint: string | null;
  /** HTTP status when it can be read from the error envelope. */
  readonly status: number | null;
  /** Original error envelope (no credentials are added by this wrapper). */
  readonly context: unknown;

  constructor(
    message: string,
    fields: {
      code?: string | null;
      details?: string | null;
      hint?: string | null;
      status?: number | null;
      context?: unknown;
    },
  ) {
    super(message);
    this.name = "RpcError";
    this.code = fields.code ?? null;
    this.details = fields.details ?? null;
    this.hint = fields.hint ?? null;
    this.status = fields.status ?? null;
    this.context = fields.context ?? null;
  }
}

function readText(source: unknown, key: string): string | null {
  if (!source || typeof source !== "object") return null;
  const value = (source as Record<string, unknown>)[key];
  return typeof value === "string" && value.trim() !== "" ? value : null;
}

function readNumber(source: unknown, key: string): number | null {
  if (!source || typeof source !== "object") return null;
  const value = (source as Record<string, unknown>)[key];
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && /^\d{3}$/.test(value)) return Number(value);
  return null;
}

/**
 * The one documented accessor for a machine-readable RPC error code.
 *
 * Classification reads this, and only this. It deliberately does NOT promote
 * an HTTP status into a code: a transport status says that some component
 * answered, not that a database transaction rolled back.
 */
export function rpcErrorCode(error: unknown): string | null {
  return readText(error, "code");
}

/**
 * HTTP status of the failed call, top-level or nested in `context`/`cause`.
 * Safe for diagnostics only — never for deciding whether a write committed.
 */
export function rpcErrorStatus(error: unknown): number | null {
  const direct = readNumber(error, "status") ?? readNumber(error, "statusCode");
  if (direct !== null) return direct;
  if (!error || typeof error !== "object") return null;
  const record = error as Record<string, unknown>;
  for (const key of ["context", "cause", "error", "originalError"]) {
    const nested = readNumber(record[key], "status") ?? readNumber(record[key], "statusCode");
    if (nested !== null) return nested;
  }
  return null;
}

/** Normalise a PostgREST error envelope without losing its machine fields. */
export function toRpcError(error: unknown): RpcError {
  const rawMessage = readText(error, "message") ?? readText(error, "error_description");
  return new RpcError(friendlyError(rawMessage), {
    code: rpcErrorCode(error),
    details: readText(error, "details"),
    hint: readText(error, "hint"),
    status: rpcErrorStatus(error),
    context: error && typeof error === "object"
      ? (error as Record<string, unknown>).context ?? null
      : null,
  });
}

/**
 * The single place the browser client learns how to call a trusted backend
 * operation and how transport errors are normalised into user-facing messages.
 *
 * Domain services (`src/lib/api/*`) build on top of this and never re-implement
 * error handling or call `supabase.rpc` directly. React components talk to the
 * domain services, never to `supabase` — so table names, RPC names and error
 * semantics live in one layer, not scattered across pages.
 *
 * RLS and RPC authorization are preserved unchanged: this only wraps the same
 * `supabase.rpc` call a page would otherwise make inline.
 */
export async function callRpc<T = unknown>(
  name: string,
  params?: Record<string, unknown>,
): Promise<T> {
  const { data, error } = await supabase.rpc(name, params ?? {});
  if (error) throw toRpcError(error);
  return data as T;
}

/**
 * Trusted RPCs that return a `{ success, error? }` *business* result (rather
 * than throwing on a business failure). Normalise that shape so a service
 * returns `data` on success and throws a friendly error otherwise.
 */
export async function callRpcResult<T>(
  name: string,
  params?: Record<string, unknown>,
  fallbackError = "That action couldn't be completed.",
): Promise<T> {
  const data = await callRpc<{ success: boolean; error?: string } & T>(name, params);
  if (!data?.success) {
    throw new Error(friendlyError(data?.error ?? fallbackError));
  }
  return data;
}